/** Single entry point to the HTTP API: cookies, JSON, typed errors, redirect on 401. */
import { purgeNavStorage } from './nav/session';
import { leaveApp } from './ui/unsavedCore';

/** `code`: the reason of a 409 / 422 when the server names one (e.g. `accepted_exists`, `version_frozen`). */
export type ApiErrorBody = { error?: string; message?: string; details?: unknown; current?: unknown; code?: string };

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: string,
    message: string,
    public readonly details?: unknown,
    public readonly current?: unknown,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Per-field messages from a validation error (`details: [{ path, message }]`). */
export function fieldIssues(error: ApiError): { path: string; message: string }[] {
  if (!Array.isArray(error.details)) return [];
  return error.details.filter(
    (d): d is { path: string; message: string } => typeof d?.path === 'string' && typeof d?.message === 'string',
  );
}

let onUnauthorized: (() => void) | null = defaultUnauthorized;

function defaultUnauthorized(): void {
  purgeNavStorage(); // P13: the lost session's drafts and places must not reach whoever signs in next
  // review I3: the app's own move — a screen's question before leaving does not stop it
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') leaveApp(() => window.location.assign('/login'));
}

/** Replaces the redirect-to-login behaviour (tests, or the app's own router). `null` disables it. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Do not redirect to /login on 401 (used by the session probe). */
  allowUnauthorized?: boolean;
  /** Cancels the request (e.g. a search overtaken by newer typing); the promise then rejects with the abort error. */
  signal?: AbortSignal;
};

const LOGIN_PATH = '/api/auth/login';

export async function api<T = void>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted) throw error;
    throw new ApiError(0, 'network', 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.');
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // not JSON (proxy error page etc.)
  }
  const error = new ApiError(
    response.status,
    body.error ?? 'unknown',
    body.message ?? 'Serwer nie odpowiedział poprawnie. Spróbuj ponownie za chwilę.',
    body.details,
    body.current,
    body.code,
  );
  if (response.status === 401 && path !== LOGIN_PATH && !options.allowUnauthorized) onUnauthorized?.();
  throw error;
}

/** Same limit as the server's (modules/files MAX_FILE_SIZE). Checked here first: the server answers an oversized upload at once and drops the connection while the browser is still sending, which under load can surface as "no connection" instead of this message. */
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Uploads one file (multipart) to POST /api/files; returns the new file's id. Errors are ApiErrors like everywhere else. */
export async function uploadFile(file: File): Promise<string> {
  if (file.size > MAX_UPLOAD_BYTES) throw new ApiError(413, 'payload_too_large', 'Plik jest za duży (limit 25 MB)');
  const body = new FormData();
  body.append('file', file);
  let response: Response;
  try {
    response = await fetch('/api/files', { method: 'POST', credentials: 'include', body });
  } catch {
    throw new ApiError(0, 'network', 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.');
  }
  let parsed: (ApiErrorBody & { id?: string }) | null = null;
  try {
    parsed = (await response.json()) as ApiErrorBody & { id?: string };
  } catch {
    // not JSON
  }
  if (response.ok && parsed?.id) return parsed.id;
  if (response.status === 401) onUnauthorized?.();
  throw new ApiError(
    response.status,
    parsed?.error ?? 'unknown',
    parsed?.message ?? 'Serwer nie odpowiedział poprawnie. Spróbuj ponownie za chwilę.',
    parsed?.details,
  );
}

/** POSTs a form with a file (multipart) and reads the JSON answer; same size check and errors as `uploadFile`. */
export async function postForm<T>(path: string, body: FormData, maxBytes = MAX_UPLOAD_BYTES): Promise<T> {
  const file = [...body.values()].find((v): v is File => v instanceof File);
  if (file && file.size > maxBytes) throw new ApiError(413, 'payload_too_large', `Plik jest za duży (limit ${Math.round(maxBytes / 1024 / 1024)} MB)`);
  let response: Response;
  try {
    response = await fetch(path, { method: 'POST', credentials: 'include', body });
  } catch {
    throw new ApiError(0, 'network', 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.');
  }
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    // not JSON
  }
  if (response.ok) return parsed as T;
  if (response.status === 401) onUnauthorized?.();
  const e = (parsed ?? {}) as ApiErrorBody;
  throw new ApiError(response.status, e.error ?? 'unknown', e.message ?? 'Serwer nie odpowiedział poprawnie. Spróbuj ponownie za chwilę.', e.details, e.current, e.code);
}
